/**
 * StarredChatButton
 *
 * Dialog for browsing and opening starred chat messages.
 *
 * Primary responsibilities:
 * - Load the current user's starred messages
 * - Group starred messages by source chat
 * - Open or remove saved starred message groups
 *
 * Dependencies:
 * - Starred messages API
 * - Split pane navigation
 * - Chat message markdown rendering
 */

import React, { useCallback, useContext, useEffect, useState } from 'react'
import { ChatbotUIContext } from '@/context/context'
import { IconMessageStar, IconStar, IconTrash, IconX } from '@tabler/icons-react'
import { PiFolderStarFill } from 'react-icons/pi'
import { Streamdown } from 'streamdown'

import { useSplitPaneNavigation } from '@/components/splitview/split-pane-context'
import { Button } from '@/components/ui/button'
import { authenticatedFetch } from '@/lib/auth/authenticated-fetch'
import { stripAssistantResponseControlMarkers } from '@/lib/utils/parse-suggestions'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import { streamdownStaticProps } from '@/components/messages/streamdown-animation'

type StarredMessage = {
  id: string
  message_id: string
  chat_id: string
  chat_name: string
  content: string
  created_at: string
}

type StarredMessagesResponse = {
  items?: StarredMessage[]
  error?: string
}

export const StarredChatButton: React.FC = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [starredMessages, setStarredMessages] = useState<StarredMessage[]>([])
  const [isLoadingStarredMessages, setIsLoadingStarredMessages] = useState(false)
  const [starredMessagesError, setStarredMessagesError] = useState<string | null>(null)
  const [pendingDeleteGroup, setPendingDeleteGroup] = useState<{
    chatId: string
    chatName: string
    count: number
  } | null>(null)
  const [deletingChatId, setDeletingChatId] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const { profile } = useContext(ChatbotUIContext)
  const { navigateToHref } = useSplitPaneNavigation()

  const fetchStarredMessages = useCallback(async () => {
    if (!profile?.user_id) {
      setStarredMessages([])
      setStarredMessagesError(null)
      return
    }

    setIsLoadingStarredMessages(true)
    setStarredMessagesError(null)

    try {
      const response = await authenticatedFetch('/api/starred-messages', {
        credentials: 'include',
        cache: 'no-store'
      })

      const data = (await response.json().catch(() => ({}))) as StarredMessagesResponse

      if (!response.ok) {
        setStarredMessages([])
        setStarredMessagesError(data.error ?? 'Failed to load starred messages.')
        return
      }

      setStarredMessages(data.items ?? [])
    } catch (error) {
      console.error('Error fetching starred messages:', error)
      setStarredMessages([])
      setStarredMessagesError('Failed to load starred messages.')
    } finally {
      setIsLoadingStarredMessages(false)
    }
  }, [profile?.user_id])

  useEffect(() => {
    if (isDialogOpen) {
      void fetchStarredMessages()
    }
  }, [fetchStarredMessages, isDialogOpen])

  const handleOpenChat = (chatId: string, messageId: string) => {
    navigateToHref(`/chat/${chatId}?highlight=${messageId}`)
    setIsDialogOpen(false)
  }

  const handleDeleteChat = async (chatId: string) => {
    setDeletingChatId(chatId)
    setDeleteError(null)

    try {
      const response = await authenticatedFetch('/api/starred-messages', {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ chat_id: chatId })
      })

      if (!response.ok) {
        const data = (await response.json().catch(() => ({}))) as StarredMessagesResponse
        console.error('Error deleting starred messages:', data.error ?? response.statusText)
        setDeleteError(data.error ?? 'Failed to remove starred messages.')
        return false
      }

      await fetchStarredMessages()
      return true
    } catch (err) {
      console.error('Unexpected error:', err)
      setDeleteError('Failed to remove starred messages.')
      return false
    } finally {
      setDeletingChatId(null)
    }
  }

  // Group messages by chat
  const groupedMessages = starredMessages.reduce(
    (acc, message) => {
      if (!acc[message.chat_id]) {
        acc[message.chat_id] = {
          chat_name: message.chat_name,
          messages: []
        }
      }
      acc[message.chat_id].messages.push(message)
      return acc
    },
    {} as Record<string, { chat_name: string; messages: typeof starredMessages }>
  )

  const renderContent = (content: string) => {
    const lines = content.split('\n')
    const clampedContent = lines.slice(0, 4).join('\n')
    const isTruncated = lines.length > 4
    let displayContent = isTruncated ? clampedContent + '...' : clampedContent

    displayContent = stripAssistantResponseControlMarkers(displayContent).trim()

    if (
      (displayContent.includes('[') && displayContent.includes(']')) ||
      displayContent.includes('**') ||
      displayContent.includes('__')
    ) {
      return (
        <Streamdown
          {...streamdownStaticProps}
          className="overflow-hidden text-sm"
          components={{
            p: ({ children }) => <p className="line-clamp-4 text-sm">{children}</p>,
            a: ({ children }) => (
              <span className="text-inherit">
                {children}
              </span>
            ),
            strong: ({ node, ...props }) => <strong {...props} className="font-bold" />,
            // Disable all other markdown elements
            h1: 'span',
            h2: 'span',
            h3: 'span',
            h4: 'span',
            h5: 'span',
            h6: 'span',
            ul: 'span',
            ol: 'span',
            li: 'span',
            blockquote: 'span',
            code: 'span',
            pre: 'span',
            img: 'span',
            em: 'span'
          }}
        >
          {displayContent}
        </Streamdown>
      )
    }
    return <p className="line-clamp-4 text-sm">{displayContent}</p>
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild className="mx-2">
        <Button
          size="icon"
          variant="ghost"
          onClick={() => setIsDialogOpen(true)}
          aria-label="Open starred messages"
          aria-expanded={isDialogOpen}
          aria-haspopup="dialog"
          title="Starred messages"
        >
          <PiFolderStarFill aria-hidden="true" stroke="3" size={27} />
        </Button>
      </DialogTrigger>
      <DialogContent className="h-[90vh] max-h-[90vh] w-[90vw] overflow-hidden rounded-lg sm:max-w-[90vw]">
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle>Starred Messages</DialogTitle>
          <div className="flex items-center space-x-2">
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setIsDialogOpen(false)}
              className="size-8"
              aria-label="Close Dialog"
            >
              <IconX aria-hidden="true" size={16} />
            </Button>
          </div>
        </DialogHeader>
        <div className="hide-scrollbar -mr-4 h-[calc(90vh-4rem)] overflow-y-auto pr-4">
          {isLoadingStarredMessages ? (
            <div
              className="text-muted-foreground flex h-32 shrink-0 flex-col items-center justify-center px-4 text-center"
              role="status"
            >
              <IconStar size={24} className="mb-2 animate-pulse" />
              <p className="mb-1 text-sm font-semibold">Loading starred messages</p>
              <p className="text-xs">Your saved messages will appear here shortly.</p>
            </div>
          ) : starredMessagesError ? (
            <div
              className="text-muted-foreground flex h-32 shrink-0 flex-col items-center justify-center px-4 text-center"
              role="alert"
            >
              <IconStar size={24} className="mb-2" />
              <p className="text-destructive mb-1 text-sm font-semibold">
                {starredMessagesError}
              </p>
              <Button size="sm" variant="outline" onClick={() => void fetchStarredMessages()}>
                Try again
              </Button>
            </div>
          ) : starredMessages.length === 0 ? (
            <div className="text-muted-foreground flex h-32 shrink-0 flex-col items-center justify-center px-4 text-center">
              <IconStar size={24} className="mb-2" />
              <p className="mb-1 text-sm font-semibold">{'No starred messages yet'}</p>
              <p className="text-xs">
                {'Star messages in your chats to see them here for quick access.'}
              </p>
            </div>
          ) : (
            <div
              className={`grid grid-cols-1 gap-4 ${
                Object.keys(starredMessages).length && Object.keys(groupedMessages).length > 1
                  ? 'sm:grid-cols-2'
                  : ''
              }`}
            >
              {Object.entries(groupedMessages).map(([chatId, { chat_name, messages }]) => (
                <div key={chatId} className="rounded-lg border p-4">
                  <div className="mb-2 flex min-w-0 items-center justify-between gap-2">
                    <p className="flex min-w-0 items-center font-semibold">
                      <IconMessageStar size={16} className="mr-2 shrink-0" />
                      <span className="truncate">{chat_name}</span>
                    </p>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => {
                        setDeleteError(null)
                        setPendingDeleteGroup({
                          chatId,
                          chatName: chat_name,
                          count: messages.length
                        })
                      }}
                      className="size-6 shrink-0"
                      aria-label={`Delete starred messages from ${chat_name}`}
                    >
                      <IconTrash aria-hidden="true" size={16} />
                    </Button>
                  </div>
                  {messages.map((message, index) => (
                    <React.Fragment key={message.id}>
                      {index > 0 && <hr className="my-2 border-gray-200" />}
                      <button
                        type="button"
                        className="focus-visible:ring-primary/40 w-full cursor-pointer rounded p-2 text-left hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2"
                        onClick={() => handleOpenChat(message.chat_id, message.id)}
                      >
                        {renderContent(message.content)}
                      </button>
                    </React.Fragment>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>

      <AlertDialog
        open={pendingDeleteGroup !== null}
        onOpenChange={(open) => {
          if (!open && !deletingChatId) {
            setDeleteError(null)
            setPendingDeleteGroup(null)
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove starred messages?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove {pendingDeleteGroup?.count ?? 0} starred
              {pendingDeleteGroup?.count === 1 ? ' message' : ' messages'} from
              {' '}
              {pendingDeleteGroup?.chatName ?? 'this chat'}.
            </AlertDialogDescription>
            {deleteError ? (
              <p className="text-destructive text-sm">{deleteError}</p>
            ) : null}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={Boolean(deletingChatId)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={Boolean(deletingChatId)}
              onClick={(event) => {
                event.preventDefault()
                if (!pendingDeleteGroup) return
                void handleDeleteChat(pendingDeleteGroup.chatId).then((removed) => {
                  if (!removed) return
                  setDeleteError(null)
                  setPendingDeleteGroup(null)
                })
              }}
            >
              {deletingChatId ? 'Removing...' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  )
}
